import store from "~/store";
import { DatabaseClient, getDatabaseClient } from '~/data/db/DatabaseClient';
import { resolveOfflineDb } from './offlineDatabase';
import QueryBuilder from "./queryBuilder";
import moment from 'moment';
import {LastPeriodicCheck, Machine, PeriodicChecksPayload, PeriodicCheckPayload} from "~/types";
import {syncTypes} from "~/sqlite/syncDataTypes";
import { syncQueue } from '~/data/sync/SyncQueue';
import { notifyPeriodicCheckQueueChanged } from '~/data/sync/periodicCheckQueue';
import { PERIODIC_CHECK_DRAFT_STATUS } from '~/app/data/statuses/periodicCheck';
import FileTransfer from "./fileTransfer";
import { applyPeriodicCheckToMachineJson, savedCheckToUiPayload } from '~/periodicCheck/offlinePeriodicCheck';

const isBlob = (value: unknown): value is Blob =>
    typeof Blob !== 'undefined' && value instanceof Blob;

export class PeriodicCheckOfflineService {

    constructor(private readonly databaseClient: DatabaseClient | null = getDatabaseClient()) {}

    private readonly qb: QueryBuilder = new QueryBuilder();
    private get SQLite() {
        return resolveOfflineDb(this.databaseClient);
    }

    public async updatePeriodicChecks(data: PeriodicChecksPayload) {
        if (!this.SQLite) {
            return false;
        }

        const checks = await this.persistCheckImages(data.checks || []);
        const machineState = store.getState().machine.data;
        const user = store.getState().user.data;
        const payload = {
            machineId: data.machineId,
            machineIntern: machineState.intern,
            projectId: data.projectId,
            inspectionType: data.inspectionType,
            checks,
            operatingTime: data.operatingTime ?? machineState.operatingTime,
            operatingCount: data.operatingCount ?? machineState.operatingCount,
            userId: user.id,
            updatedAt: moment().format("YYYY-MM-DD HH:mm:ss"),
            guid: data.guid,
            isDraft: data.isDraft ?? 0,
            ...(data.id ? { id: data.id } : {}),
        };

        const payloadJson = JSON.stringify(payload);
        const uiChecks = checks.map(check => savedCheckToUiPayload(check as PeriodicCheckPayload));

        await syncQueue.enqueueReplace(
            [
                {
                    data: payloadJson,
                    userId: Number(user.id),
                    typeOfData: syncTypes.ADD_CHECK_PERIOD,
                },
            ],
            (existing, next) =>
                String(existing.machineId) === String(next.machineId) &&
                String(existing.inspectionType) === String(next.inspectionType)
        );
        notifyPeriodicCheckQueueChanged();
        await this.updateMachineLastPeriodicCheck(data, user, uiChecks);
        return true;
    }

    private updateMachineLastPeriodicCheck(
        data: PeriodicChecksPayload,
        user: { id: string | number; name: string },
        uiChecks: PeriodicCheckPayload[],
    ): Promise<void> {
        const db = this.SQLite;
        if (!db) {
            return Promise.resolve();
        }
        const parentId = String(store.getState().machine.data?.id || data.machineId);
        const equipmentId = String(data.machineId);
        const rowIds = Array.from(new Set([parentId, equipmentId]));
        const isDraft = !!data.isDraft;
        const lastCheck: LastPeriodicCheck = {
            id: data.id ? String(data.id) : `local-${data.guid || equipmentId}`,
            userId: user.id,
            userName: user.name,
            date: moment().format('YYYY-MM-DD'),
            ...(isDraft ? { status: PERIODIC_CHECK_DRAFT_STATUS } : { pendingSync: true }),
        };

        return new Promise((resolve, reject) => {
            db.transaction(
                (tx) => {
                    rowIds.forEach((rowId) => {
                        const where = [{ name: 'id', value: rowId }];
                        const machineSelect = this.qb.select('Machine', [], where);
                        tx.executeSql(machineSelect.query, machineSelect.params, (_machineTx, machineResult) => {
                            const excavatorData = this.qb.getSingleResult(machineResult);
                            if (!excavatorData || !excavatorData.data) {
                                return;
                            }
                            let excavatorDetails: Machine;
                            try {
                                excavatorDetails = JSON.parse(excavatorData.data);
                            } catch {
                                return;
                            }
                            const applied = applyPeriodicCheckToMachineJson(
                                excavatorDetails,
                                equipmentId,
                                Number(data.inspectionType),
                                lastCheck,
                                uiChecks,
                                data.projectId,
                            );
                            if (!applied.changed) {
                                return;
                            }
                            const update = this.qb.update(
                                'Machine',
                                { data: JSON.stringify(applied.machine) },
                                where
                            );
                            tx.executeSql(update.query, update.params);
                        });
                    });
                },
                (e) => reject(e),
                () => resolve()
            );
        });
    }

    private async persistCheckImages(checks: PeriodicChecksPayload['checks']) {
        const fileTransfer = new FileTransfer();
        return Promise.all(checks.map(async (check) => {
            const newImages = Array.isArray(check.newImages) ? check.newImages : [];
            const existingPaths = newImages.filter((image): image is string => typeof image === 'string');
            const files = newImages.filter(isBlob);
            const saved = files.length
                ? await fileTransfer.saveFilesFromBlobs(files as File[], 'periodicCheckImages')
                : [];
            return {
                ...check,
                newImages: existingPaths.concat(saved),
            };
        }));
    }
}
